import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getNutritionPlans, addMeal } from '../../utils/api';
import { Box, Radio, RadioGroup, FormControlLabel, Typography, List, ListItem, ListItemText, Button } from '@mui/material';
import Modal from 'react-modal';
import Loader from '../Loader';

// Set the app element for react-modal (assuming it's set globally elsewhere, but included here for completeness)
Modal.setAppElement('#root');

const StartNutritionPlan = ({ userId }) => {
    const { planId } = useParams();
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [consumedStatus, setConsumedStatus] = useState({});
    const [isAddToDailyModalOpen, setIsAddToDailyModalOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    const mealTypeMapping = {
        'breakfast': 'ארוחת בוקר',
        'lunch': 'ארוחת צהריים',
        'dinner': 'ארוחת ערב',
        'snack': 'חטיפים'
    };

    // Fetch the nutrition plan
    useEffect(() => {
        const fetchPlan = async () => {
            try {
                const plans = await getNutritionPlans(userId);
                const selectedPlan = plans.find(p => p.nutritionPlanId._id === String(planId));
                if (selectedPlan) {
                    setPlan(selectedPlan.nutritionPlanId);
                } else {
                    console.error('תוכנית לא נמצאה');
                }
                setLoading(false);
            } catch (error) {
                console.error('שגיאה בטעינת תוכנית תזונה:', error);
                setLoading(false);
            }
        };
        fetchPlan();
    }, [userId, planId]);

    // Initialize consumedStatus when plan loads
    useEffect(() => {
        if (plan) {
            const initialConsumedStatus = {};
            plan.meals.forEach(meal => {
                initialConsumedStatus[meal._id] = null; // No selection initially
            });
            setConsumedStatus(initialConsumedStatus);
        }
    }, [plan]);

    // Handle radio button selection and open modal
    const handleRadioChange = (mealId, optionId) => {
        setConsumedStatus(prev => ({
            ...prev,
            [mealId]: optionId
        }));
        const meal = plan.meals.find(m => m._id === mealId);
        const option = meal.options.find(o => o._id === optionId);
        setSelectedOption({ meal, option });
        setIsAddToDailyModalOpen(true);
    };

    // Handle adding the meal to daily tracking
    const handleAddToDaily = async () => {
        if (!selectedOption) return;
        const { meal, option } = selectedOption;

        // Create a food object compatible with addMeal
        const food = {
            description: option.description,
            brandOwner: "Nutrition Plan",
            weight: "N/A", // Weight not available in nutrition plan data
            calories: option.calories,
            ingredients: "N/A", // Ingredients not specified
            foodNutrients: [
                { nutrientName: "Energy", value: option.calories, unitName: "kcal" },
                { nutrientName: "Protein", value: option.proteins, unitName: "g" },
                { nutrientName: "Carbohydrate, by difference", value: option.carbs, unitName: "g" },
                { nutrientName: "Total lipid (fat)", value: option.fats, unitName: "g" },
            ],
            mealType: meal.mealType,
        };

        try {
            await addMeal(userId, food, meal.mealType);
            setIsAddToDailyModalOpen(false);
            // Optionally, you could add a success message here
        } catch (error) {
            console.error('שגיאה בהוספת המנה למעקב היומי:', error);
            // Optionally, handle the error with a user-facing message
        }
    };

    if (loading) return <Loader />;
    if (!plan) return <Typography>תוכנית לא נמצאה</Typography>;

    return (
        <Box sx={{ padding: '20px', direction: 'rtl', textAlign: 'right' }}>
            <Typography
                variant="h5"
                sx={{ mb: 2, fontFamily: 'GveretLevin' }}
            >
                {plan.planName}
            </Typography>
            {plan.meals.map(meal => (
                <Box key={meal._id} sx={{ margin: '10px 0' }}>
                    <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold', fontFamily: 'GveretLevin' }}
                    >
                        {mealTypeMapping[meal.mealType] || meal.mealType}
                    </Typography>
                    <RadioGroup
                        value={consumedStatus[meal._id] || ''}
                        onChange={(e) => handleRadioChange(meal._id, e.target.value)}
                    >
                        <List sx={{ direction: 'rtl', textAlign: 'right' }}>
                            {meal.options.map(option => (
                                <ListItem key={option._id}>
                                    <FormControlLabel
                                        value={option._id}
                                        control={<Radio />}
                                        label={
                                            <ListItemText
                                                primary={option.name + ': ' + option.description}
                                                secondary={`קלוריות: ${option.calories}, חלבון: ${option.proteins} גרם, פחמימות: ${option.carbs} גרם, שומן: ${option.fats} גרם`}
                                                primaryTypographyProps={{ style: { fontFamily: 'GveretLevin', textAlign: 'right' } }}
                                                secondaryTypographyProps={{ style: { fontFamily: 'GveretLevin', textAlign: 'right' } }}
                                            />
                                        }
                                        labelPlacement="end"
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </RadioGroup>
                </Box>
            ))}

            {/* Modal for adding to daily caloric meals */}
            <Modal
                isOpen={isAddToDailyModalOpen}
                onRequestClose={() => setIsAddToDailyModalOpen(false)}
                style={{
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                    content: {
                        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
                        transform: 'translate(-50%, -50%)', width: '400px', maxWidth: '90vw',
                        padding: '20px', borderRadius: '8px', backgroundColor: '#fff',
                        direction: 'rtl', textAlign: 'right'
                    },
                }}
            >
                <Typography variant="h6" sx={{ fontFamily: 'GveretLevin' }}>
                    הוספת מנה לארוחות היומיות
                </Typography>
                <Typography sx={{ mt: 2 }}>
                    האם ברצונך להוסיף את המנה "{selectedOption?.option?.description}" לארוחות הקלוריות היומיות שלך?
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button onClick={handleAddToDaily} sx={{ fontFamily: 'GveretLevin' }}>
                        כן
                    </Button>
                    <Button onClick={() => setIsAddToDailyModalOpen(false)} sx={{ fontFamily: 'GveretLevin' }}>
                        לא
                    </Button>
                </Box>
            </Modal>
        </Box>
    );
};

export default StartNutritionPlan;